import { useState, useEffect } from 'react'

interface ExtendedNotificationOptions extends NotificationOptions {
  vibrate?: number[]
}

interface NotificationHook {
  permission: NotificationPermission
  requestPermission: () => Promise<NotificationPermission>
  sendNotification: (title: string, options?: ExtendedNotificationOptions) => void
  scheduleReminder: (time: string) => void
  clearReminders: () => void
}

export function useNotifications(): NotificationHook {
  const [permission, setPermission] = useState<NotificationPermission>('default')

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission)
    }
  }, [])

  const requestPermission = async (): Promise<NotificationPermission> => {
    if ('Notification' in window) {
      const result = await Notification.requestPermission()
      setPermission(result)
      return result
    }
    return 'denied'
  }

  const sendNotification = (title: string, options?: ExtendedNotificationOptions) => {
    if (permission === 'granted') {
      // Create base options without unsupported properties
      const baseOptions: NotificationOptions = {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        ...options
      }

      // Remove unsupported properties for the Notification constructor
      const { vibrate, ...notificationOptions } = baseOptions as any

      const notification = new Notification(title, notificationOptions)

      // Handle vibration separately if supported
      if (vibrate && 'vibrate' in navigator) {
        navigator.vibrate(vibrate)
      }

      notification.onclick = () => {
        window.focus()
        notification.close()
      }

      // Auto close after 5 seconds
      setTimeout(() => notification.close(), 5000)
    }
  }

  const scheduleReminder = (time: string) => {
    if ('serviceWorker' in navigator && permission === 'granted') {
      navigator.serviceWorker.ready.then((registration) => {
        // Schedule daily reminder
        const now = new Date()
        const [hours, minutes] = time.split(':').map(Number)
        const reminderTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes)
        
        if (reminderTime <= now) {
          reminderTime.setDate(reminderTime.getDate() + 1)
        }

        const delay = reminderTime.getTime() - now.getTime()
        
        setTimeout(() => {
          sendNotification('Gitaigo Reminder', {
            body: 'Come ti senti oggi? Non dimenticare di tracciare il tuo umore! 😊',
            tag: 'mood-reminder',
            requireInteraction: false
          })
          
          // Schedule next day
          scheduleReminder(time)
        }, delay)
      })
    }
  }

  const clearReminders = () => {
    // Clear any existing reminders
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.getNotifications({ tag: 'mood-reminder' }).then((notifications) => {
          notifications.forEach(notification => notification.close())
        })
      })
    }
  }

  return {
    permission,
    requestPermission,
    sendNotification,
    scheduleReminder,
    clearReminders
  }
}